/* eslint-disable complexity */
/* eslint-disable max-depth */
/* eslint-disable max-len */
import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { chain, debounce, extend, has, isEmpty, isFunction } from 'lodash';
import { ConnectedLocationsList } from './LocationsList';
import {
    ABUtils,
    Config,
    CurrentAccommodationSearchSessionStorage,
    LocationUtils,
    TrackingUtils,
    withPageContext
} from '../../utils';
import { LocationNotFound } from './LocationNotFound';
import {
    GeoNodeType,
    LocationUserSubscription,
    LocationType,
    LegTypeProps,
    EntryPoint,
    TabTypeProp,
    TabTypePropDefault,
    SourceOptionsType,
    LegType,
    LocationHotelsSuppliers,
} from '../../models';
import {
    getLocationAutocomplete,
    getHotelLocationAutocomplete,
    getDpLocationAutocompleteGeoRegion,
} from '../../redux/locationAutocomplete/actions';
import {
    DP_SEARCH_FORM_DEPARTURE_NO_MATCHES,
    DP_SEARCH_FORM_DESTINATION_NO_MATCHES,
    HSA_SEARCH_FORM_ROOMS_NO_MATCHES,
    withTrackComponentDisplayed
} from '../../utils/HtmlEvents';
import { LocationsListSkeleton } from './LocationsListSkeleton';
import { useSearchId } from '../SearchManager/SearchIdProvider';

const supplier = LocationHotelsSuppliers.HSA;
const getLocationNotFoundWithTrackComponentDisplayed = (activeProduct, departureOrArrival) => {
    let LocationNotFoundWithTrackComponentDisplayed = LocationNotFound;
    if (activeProduct === EntryPoint.HOTELS) {
        LocationNotFoundWithTrackComponentDisplayed = withTrackComponentDisplayed(LocationNotFound, HSA_SEARCH_FORM_ROOMS_NO_MATCHES);
    } else if (activeProduct === EntryPoint.DP) {
        const htmlEventID = departureOrArrival === LegType.ARRIVAL ?
            DP_SEARCH_FORM_DESTINATION_NO_MATCHES : DP_SEARCH_FORM_DEPARTURE_NO_MATCHES;
        LocationNotFoundWithTrackComponentDisplayed = withTrackComponentDisplayed(LocationNotFound, htmlEventID);
    }
    return LocationNotFoundWithTrackComponentDisplayed;
};

const showLocationsListSkeleton = (activeProduct, completedFetch, prevSuggestionsListRef) => {
    return !completedFetch && activeProduct === EntryPoint.HOTELS && isEmpty(prevSuggestionsListRef.current);
};

const showPrevSuggestionsList = (completedFetch, prevSuggestionsListRef) => {
    return !completedFetch && !isEmpty(prevSuggestionsListRef.current);
};

const LocationAutocomplete = ({
    displayedWord,
    hasSelectedValue,
    onClickItem,
    departureOrArrival,
    fetchedLocations,
    isFixedWidth,
    activeProduct,
    userAuthentication,
    getLocationAutocomplete,
    getHotelLocationAutocomplete,
    getDpLocationAutocompleteGeoRegion,
    trackEvent,
    propsAutocompleteOptions,
}) => {
    const { updateSearchId } = useSearchId();
    const autocompleteOptions = propsAutocompleteOptions || Config.get('autocompleteOptions');
    const { isDpGeoRegionAutocomplete, isNewItineraryAutocomplete = false } = ABUtils.getABs() || {};
    const usesGeoRegionAutocompleteFlow =
        activeProduct === EntryPoint.HOTELS ||
        (activeProduct === EntryPoint.DP && departureOrArrival === LegType.ARRIVAL && isDpGeoRegionAutocomplete);

    const getUserSubscription = (userAuthentication) => {
        const {
            usersResponse: { membership: isMembership },
        } = userAuthentication;
        return isMembership ? LocationUserSubscription.PRIME : LocationUserSubscription.NON_PRIME;
    };

    const getDisplayableSuggestedLocations = (fetchedLocations) => {
        const locationProductType = LocationUtils.getLocationProductType(
            departureOrArrival,
            activeProduct,
        );
        const filterParams = {
            fetchedLocations,
            displayedWord,
            maxAutocompleteSuggestions: LocationUtils.getAutocompleteMaxSuggestions(activeProduct, autocompleteOptions),
            maxAccommodationAutocompletesSuggestions: LocationUtils.getAutocompleteMaxAccommodationsSuggestions(activeProduct, autocompleteOptions),
            locationProductType,
            filterByDisplayedWord: !isNewItineraryAutocomplete,
        };
        const filteredLocations = usesGeoRegionAutocompleteFlow
            ? LocationUtils.filterSuggestedLocationsSearchByRegion({
                ...filterParams,
            })
            : LocationUtils.filterSuggestedLocations({
                ...filterParams,
            });

        return filteredLocations.map((location) => {
            let values = [location.name];
            let match;

            if (location.type !== LocationType.REGION && Array.isArray(location.locationNames)) {
                values = values.concat(location.locationNames);
            }

            match = values.find((name) => name.toLowerCase().indexOf(displayedWord) >= 0);

            fillDisplayName(location);

            return chain(location)
                .extend({
                    displayName:
                        (location.geoNodeType === GeoNodeType.CITY &&
                        location.type === LocationType.IATA_CODE
                            ? location.city
                            : location.name) || match,
                })
                .tap(formatLocation)
                .value();
        });
    };

    const getSuggestionList = (locations) => {
        return getDisplayableSuggestedLocations(locations);
    };


    const formatLocation = (loc) => {
        const formatter = {
            AIRPORT: formatCityOrAirportTypeText,
            CITY: formatCityOrAirportTypeText,
            IATA_CODE: formatIataTypeText,
        };
        let type;
        if (loc.relatedLocations?.length > 1) {
            loc.relatedLocations.forEach((rel) => {
                type = rel.geoNodeType || rel.type;
                extend(rel, {
                    text: has(formatter, type.toUpperCase())
                        ? formatter[type.toUpperCase()](rel)
                        : '',
                });
            });
        }
        if (!has(loc, 'displayName') || isEmpty(loc.displayName)) {
            extend(loc, {
                displayName: loc.city,
            });
        }
        type = loc.geoNodeType || loc.type || LocationType.CITY;

        return extend(loc, {
            text: isFunction(formatter[type.toUpperCase()])
                ? formatter[type.toUpperCase()](loc)
                : '',
        });
    };

    const formatIataTypeText = (loc) => {
        const { relatedLocations, displayName, name } = loc;
        return !isEmpty(relatedLocations)
            ? `${displayName} - ${relatedLocations[0].name}`
            : `${displayName} - ${name}`;
    };

    const formatCityOrAirportTypeText = (loc) => {
        return loc.displayName || loc.name;
    };

    const fillDisplayName = (location) => {
        if (!isEmpty(location.relatedLocations)) {
            location.relatedLocations.forEach((rel) => {
                extend(rel, {
                    displayName: rel.name,
                });
            });
        }
    };

    const locationProductType = LocationUtils.getLocationProductType(
        departureOrArrival,
        activeProduct,
    );
    const subscription = getUserSubscription(userAuthentication);
    const fetchedLocation = LocationUtils.getFetchedLocations(
        fetchedLocations,
        locationProductType,
        departureOrArrival,
        activeProduct,
        displayedWord,
        subscription,
        autocompleteOptions,
        isNewItineraryAutocomplete,
    );
    const hasRequestError = fetchedLocation?.requestStatus === 'error';
    const completedFetch = fetchedLocation && !fetchedLocation.isFetching && !hasRequestError;
    const loadTime = fetchedLocation?.executionTime;
    const locations = completedFetch ? fetchedLocation.locationAutocomplete : [];
    const suggestionsList = getSuggestionList(locations);
    const prevSuggestionsListRef = useRef([]);
    const fetchStartTimeRef = useRef(null);
    const initialHasSelectedValueRef = useRef(hasSelectedValue);
    const autocompleteTrackingSourceRef = useRef(hasSelectedValue ? 'prefilled' : 'typed');
    if (initialHasSelectedValueRef.current && !hasSelectedValue) {
        autocompleteTrackingSourceRef.current = 'edited';
    }
    const autocompleteTrackingSource = autocompleteTrackingSourceRef.current;
    const autocompleteExposureTrackedRef = useRef(new Set());
    const autocompleteListStatesTrackedRef = useRef(new Set());
    const autocompleteSelectionTrackedRef = useRef(false);

    const isKeywordLongEnough = LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
        displayedWord,
        autocompleteOptions,
        activeProduct,
        departureOrArrival,
    );

    const handleTracking = useCallback((label) => {
        const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
        (activeProduct === EntryPoint.HOTELS || activeProduct === EntryPoint.DP) && trackEvent({ action, label });
    }, [activeProduct, trackEvent]);

    const isTrainsOrFlights = activeProduct === EntryPoint.TRAINS || activeProduct === EntryPoint.FLIGHTS;
    const trackingFieldType = departureOrArrival === LegType.ARRIVAL ? 'dest' : 'orig';
    const trackingAction = activeProduct === EntryPoint.FLIGHTS ? 'searcher_flights' : 'searcher_trains';
    const pageCategory = 'home';
    const ensureAutocompleteExposure = useCallback((source) => {
        if (!isTrainsOrFlights || autocompleteExposureTrackedRef.current.has(source)) {
            return;
        }
        autocompleteExposureTrackedRef.current.add(source);
        trackEvent({
            action: trackingAction,
            label: TrackingUtils.LABEL.autocompleteExposureV1({
                field: trackingFieldType,
                source,
            }),
        });
    }, [isTrainsOrFlights, trackEvent, trackingAction, trackingFieldType]);

    useEffect(() => {
        if (!isKeywordLongEnough) {
            prevSuggestionsListRef.current = [];
        }
        if (isKeywordLongEnough && completedFetch) {
            if (loadTime) {
                handleTracking(TrackingUtils.LABEL.hotelsDestinationTyped('', supplier));
                handleTracking(TrackingUtils.LABEL.hotelsDestinationLoadTime(loadTime, supplier));
            }

            if (isTrainsOrFlights) {
                if (!isEmpty(suggestionsList) && fetchStartTimeRef.current) {
                    const validLoadTime = loadTime
                        ? (loadTime / 1000)
                        : ((Date.now() - fetchStartTimeRef.current) / 1000);

                    trackListEvent({
                        trackEvent,
                        category: pageCategory,
                        action: trackingAction,
                        label: `searchbox_list-time:${validLoadTime.toFixed(2)}_${trackingFieldType}`,
                    });

                    fetchStartTimeRef.current = null;
                } else if (isEmpty(suggestionsList)) {
                    trackListEvent({
                        trackEvent,
                        category: pageCategory,
                        action: trackingAction,
                        label: `searchbox_dest_error_no_matches:${trackingFieldType}`,
                    });
                }
            }

            if (isEmpty(suggestionsList)) {
                handleTracking(
                    TrackingUtils.LABEL.inputErrorNoMatches({
                        activeProduct,
                        inputField: departureOrArrival === LegType.ARRIVAL ? 'dest' : 'dep',
                        searchValue: displayedWord,
                        supplier,
                    }),
                );
            }
            prevSuggestionsListRef.current = suggestionsList;
        }
    }, [isKeywordLongEnough, completedFetch, suggestionsList, departureOrArrival, loadTime, isTrainsOrFlights, handleTracking, trackEvent, trackingAction, trackingFieldType, activeProduct, displayedWord]);

    useEffect(() => {
        if (!isTrainsOrFlights || !isKeywordLongEnough) {
            return;
        }
        let state;
        if (hasRequestError) {
            state = 'error';
        } else if (completedFetch) {
            state = isEmpty(suggestionsList) ? 'empty' : 'shown';
        }
        const trackedState = `${autocompleteTrackingSource}:${state}`;
        if (!state || autocompleteListStatesTrackedRef.current.has(trackedState)) {
            return;
        }
        ensureAutocompleteExposure(autocompleteTrackingSource);
        autocompleteListStatesTrackedRef.current.add(trackedState);
        trackEvent({
            action: trackingAction,
            label: TrackingUtils.LABEL.autocompleteListV1({
                field: trackingFieldType,
                source: autocompleteTrackingSource,
                state,
                queryLength: displayedWord.length,
            }),
        });
    }, [autocompleteTrackingSource, completedFetch, displayedWord.length, ensureAutocompleteExposure, hasRequestError, isKeywordLongEnough, isTrainsOrFlights, suggestionsList, trackEvent, trackingAction, trackingFieldType]);

    const _onClickItem = (currentSearch, { position: visualPosition } = {}) => {
        const selectedSearch = usesGeoRegionAutocompleteFlow
            ? { ...currentSearch, sourceProduct: activeProduct }
            : currentSearch;
        const { name, value, displayName } = currentSearch;
        const itemName = displayName || name || value;

        handleTracking(TrackingUtils.LABEL.hotelsDestinationTyped(displayedWord, supplier));

        const position = findItemPosition(suggestionsList, currentSearch);

        if (isTrainsOrFlights) {
            ensureAutocompleteExposure(autocompleteTrackingSource);
            if (!autocompleteSelectionTrackedRef.current) {
                autocompleteSelectionTrackedRef.current = true;
                trackEvent({
                    action: trackingAction,
                    label: TrackingUtils.LABEL.autocompleteSelectV1({
                        field: trackingFieldType,
                        source: autocompleteTrackingSource,
                        position: visualPosition,
                        queryLength: displayedWord.length,
                    }),
                });
            }
            trackEvent({
                category: pageCategory,
                action: trackingAction,
                label: `searchbox_type:${trackingFieldType}_${displayedWord.length}`
            });

            trackEvent({
                category: pageCategory,
                action: trackingAction,
                label: `searchbox_select:${trackingFieldType}_pos:${position + 1}_dest:${itemName}_type:${displayedWord}`
            });
        }

        if (activeProduct === EntryPoint.HOTELS) {
            CurrentAccommodationSearchSessionStorage.setSegment({
                destination: selectedSearch,
            });
            updateSearchId();
        }
        handleTracking(
            TrackingUtils.LABEL.hotelsDestinationSelectPos(
                position + 1,
                currentSearch,
                supplier,
            ),
        );
        onClickItem(selectedSearch);
    };

    const findItemPosition = (suggestionsList, currentSearch) => {
        let visualPosition = 0;

        for (const item of suggestionsList) {
            if (item.relatedLocations?.length) {
                if (matchesItem(item, currentSearch)) {
                    return visualPosition;
                }

                for (const relatedItem of item.relatedLocations) {
                    if (matchesItem(relatedItem, currentSearch)) {
                        return visualPosition;
                    }
                    visualPosition++;
                }
            } else {
                if (matchesItem(item, currentSearch)) {
                    return visualPosition;
                }
                visualPosition++;
            }
        }

        return 0;
    };

    const matchesItem = (item, currentSearch) => {
        if (currentSearch.geoNodeId && item.geoNodeId) {
            return item.geoNodeId === currentSearch.geoNodeId;
        }

        if (currentSearch.iata && item.iata) {
            return item.iata === currentSearch.iata;
        }

        return item.name === currentSearch.name ||
           item.value === currentSearch.value ||
           item.displayName === currentSearch.displayName;
    };

    const triggerAutocomplete = useCallback(
        debounce((displayedWord) => {
            const subscription = userAuthentication.usersResponse.membership
                ? LocationUserSubscription.PRIME
                : LocationUserSubscription.NON_PRIME;
            const isDpArrival =
                activeProduct === EntryPoint.DP && departureOrArrival === LegType.ARRIVAL;

            if (isDpArrival && isDpGeoRegionAutocomplete) {
                getDpLocationAutocompleteGeoRegion({
                    activeProduct,
                    displayedWord,
                    autocompleteOptions,
                });
            } else if (activeProduct === EntryPoint.HOTELS) {
                getHotelLocationAutocomplete({
                    activeProduct,
                    displayedWord,
                    autocompleteOptions,
                    subscription,
                });
            } else {
                const locationProductType = LocationUtils.getLocationProductType(
                    departureOrArrival,
                    activeProduct,
                );
                getLocationAutocomplete({
                    activeProduct,
                    displayedWord,
                    autocompleteOptions,
                    departureOrArrival,
                    locationProductType,
                    useFullSearch: isNewItineraryAutocomplete,
                });
            }
        }, 300),
        [activeProduct, autocompleteOptions, departureOrArrival, getLocationAutocomplete, getHotelLocationAutocomplete, getDpLocationAutocompleteGeoRegion, isDpGeoRegionAutocomplete, isNewItineraryAutocomplete, userAuthentication.usersResponse.membership]
    );

    useEffect(() => () => triggerAutocomplete.cancel(), [triggerAutocomplete]);

    useEffect(() => {
        if (
            LocationUtils.isKeywordLongEnoughAccordingWithLanguage(
                displayedWord,
                autocompleteOptions,
                activeProduct,
                departureOrArrival,
            )
        ) {
            fetchStartTimeRef.current = Date.now();
            triggerAutocomplete(displayedWord);
        }
    }, [activeProduct, autocompleteOptions, displayedWord, triggerAutocomplete]);

    let content;
    if (!isKeywordLongEnough) {
        content = null;
    } else if (showLocationsListSkeleton(activeProduct, completedFetch, prevSuggestionsListRef)) {
        const numberOfSkeletons = LocationUtils.getAutocompleteMaxSuggestions(activeProduct, autocompleteOptions);
        return <LocationsListSkeleton numberOfSkeletons={numberOfSkeletons} />;
    } else if (showPrevSuggestionsList(completedFetch, prevSuggestionsListRef)) {
        content = (
            <ConnectedLocationsList
                displayedWord={displayedWord}
                departureOrArrival={departureOrArrival}
                suggestionsList={prevSuggestionsListRef.current}
                onClickItem={_onClickItem}
                isFixedWidth={isFixedWidth}
            />
        );
    } else if (completedFetch && !isEmpty(suggestionsList)) {
        content = (
            <ConnectedLocationsList
                displayedWord={displayedWord}
                departureOrArrival={departureOrArrival}
                suggestionsList={suggestionsList}
                onClickItem={_onClickItem}
                isFixedWidth={isFixedWidth}
            />
        );
    } else if (completedFetch) {
        let LocationNotFoundWithTrackComponentDisplayed = getLocationNotFoundWithTrackComponentDisplayed(activeProduct, departureOrArrival);
        content = (
            <LocationNotFoundWithTrackComponentDisplayed
                activeProduct={activeProduct}
                displayedWord={displayedWord}
            />
        );
    }
    return (<>{content}</>);
};

LocationAutocomplete.propTypes = {
    ...SourceOptionsType,
    hasSelectedValue: PropTypes.bool,
    departureOrArrival: LegTypeProps.isRequired,
    fetchedLocations: PropTypes.object.isRequired,
    getLocationAutocomplete: PropTypes.func.isRequired,
    getHotelLocationAutocomplete: PropTypes.func.isRequired,
    getDpLocationAutocompleteGeoRegion: PropTypes.func.isRequired,
    activeProduct: TabTypeProp,

    autocompleteOptions: PropTypes.shape({
        accommodationAutocompleteMinChars: PropTypes.number.isRequired,
        accommodationAutocompleteMaxSuggestions: PropTypes.number,
        accommodationAutocompleteMaxAccommodations: PropTypes.number,
        autocompleteMinChars: PropTypes.number.isRequired,
        defaultMinChars: PropTypes.number.isRequired,
        autocompleteMaxSuggestions: PropTypes.number.isRequired,
        coherentCharsRegex: PropTypes.string.isRequired,
    }),
};

LocationAutocomplete.defaultProps = {
    activeProduct: TabTypePropDefault,
    autocompleteOptions: null,
    hasSelectedValue: false,
};

const mapStateToProps = ({ locationAutocomplete, activeProduct, userAuthentication }) => ({
    fetchedLocations: locationAutocomplete,
    activeProduct: activeProduct.activeProduct,
    userAuthentication: userAuthentication,
});

const connected = connect(mapStateToProps, {
    getLocationAutocomplete,
    getHotelLocationAutocomplete,
    getDpLocationAutocompleteGeoRegion,
})(withPageContext(LocationAutocomplete));

export { connected as ConnectedLocationAutocomplete };

// Extrae helpers fuera del componente si los usas en más sitios
const trackListEvent = ({ trackEvent, category, action, label }) => {
    if (action && label) {
        trackEvent({ category, action, label });
    }
};
